/**
 * Texas licence class rules for the Create case form, kept free of imports so backend tests can
 * load them without pulling in React components. Mirrors backend/src/utils/licenseClass.ts.
 */
export type CaseLicenseType = '' | 'Class C' | 'CDL' | 'None';
export type ClientLicenseClass = '' | 'CDL' | 'Regular';

/**
 * The case's licence type in the form's terms, or '' when the document said something else.
 *
 * Lloyd, 31 Oct 2026: "anything in Texas that starts with A or B are commercial. Anything with a C
 * is a class C. An M, by itself or added to something, simply means he has a Motorcycle license as
 * well. In this case, he has AM, which means commercial with motorcycle. Since we don't care if he
 * has a motorcycle or not, it should be labeled as simply CDL."
 * M alone is non-commercial, so it maps to Class C / Regular.
 */
export function caseLicenseType(raw: string | null | undefined): CaseLicenseType {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();

  if (/^(none|no\s*licen[cs]e|unlicensed)$/i.test(lower)) return 'None';
  if (
    /\bnon[\s-]*commercial\b/i.test(lower) ||
    /\bnon[\s-]*cdl\b/i.test(lower) ||
    /^(regular|standard|ordinary)$/i.test(lower)
  ) {
    return 'Class C';
  }
  if (/\bcdl\b/i.test(lower) || /\bcommercial\b/i.test(lower)) return 'CDL';

  // Strip "class" before checking letters so the "A" in "CLASS C" never matches commercial A.
  const code = lower
    .replace(/\b(class|dl|driver'?s?|licen[cs]e|type|endorsement)\b/gi, '')
    .replace(/[^a-z0-9]/gi, '')
    .toUpperCase();
  if (!code) return '';
  if (/^[ABCM]+$/.test(code)) {
    if (code.includes('A') || code.includes('B')) return 'CDL';
    if (code.includes('C') || code.includes('M')) return 'Class C';
  }
  return '';
}

export function clientLicenseClass(raw: string | null | undefined): ClientLicenseClass {
  const mapped = caseLicenseType(raw);
  if (mapped === 'CDL') return 'CDL';
  if (mapped === 'Class C') return 'Regular';
  return '';
}
